import React, { useEffect, useState } from 'react';

import { RiRoadMapLine } from '@remixicon/react';

import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import { Field } from '@/design-system/field';
import { FormModal } from '@/design-system/form-modal';
import { Hint } from '@/design-system/hint';
import { Input, InputRoot } from '@/design-system/input';
import { Label } from '@/design-system/label';
import { toast } from '@/design-system/toaster';
import { moneyDigits } from '@/pages/ujp/components/ujpForm';
import {
  isWorkflowDrift,
  planCostItems,
  RoutePlanApiError,
  routePlansAPI,
  type RoutePlan,
  type RoutePlanDrift,
  type RoutePlanLaneWriteBack,
} from '@/services/api/routePlans';

import {
  defaultPlanDraft,
  fromPlan,
  planDraftErrors,
  toLegsPayload,
  toStopsPayload,
  type PlanDraft,
} from './routePlanDraft';
import RoutePlanBuilder from './RoutePlanBuilder';
import RoutePlanCostItems, {
  emptyCostDrafts,
  foldedCostNote,
  toCostDrafts,
  toCostItemsPayload,
  type CostItemDraft,
} from './RoutePlanCostItems';

/**
 * RoutePlanDrawer (CR-2) — create or edit a route plan in a right-side
 * `FormModal`. Hosts the `RoutePlanBuilder`; on save the manual stops are
 * written back to Addresses as DRAFT lanes best-effort (CR2-D8), so a partial
 * write-back keeps the drawer open with a warning + "Coba lagi" that re-saves
 * the plan and re-attempts the lanes.
 */

export interface RoutePlanDrawerProps {
  open: boolean;
  onClose: () => void;
  clientId: number | null;
  clientName?: string;
  chargedPositioning: boolean | null;
  /** Edit mode when set; create mode otherwise. */
  plan?: RoutePlan | null;
  /** Fires once the plan is saved AND its lanes are fully written back. */
  onSaved: (plan: RoutePlan) => void;
  /** Skip the server legs call (tests). */
  enableLegs?: boolean;
}

const RoutePlanDrawer: React.FC<RoutePlanDrawerProps> = ({
  open,
  onClose,
  clientId,
  clientName,
  chargedPositioning,
  plan,
  onSaved,
  enableLegs = true,
}) => {
  const editing = !!plan;
  const [name, setName] = useState('');
  const [draft, setDraft] = useState<PlanDraft>(() => defaultPlanDraft());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [writeBack, setWriteBack] = useState<RoutePlanLaneWriteBack | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [drift, setDrift] = useState<RoutePlanDrift[]>([]);
  const [costRows, setCostRows] = useState<CostItemDraft[]>(emptyCostDrafts);
  const [notes, setNotes] = useState('');
  // Digits; '' = no override (ASSESSMENT-ROUTE-PLAN-BBM-OVERRIDE).
  const [bbmFixOverride, setBbmFixOverride] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(plan?.name ?? '');
    setDraft(plan ? fromPlan(plan, chargedPositioning ?? false) : defaultPlanDraft());
    setErrors({});
    setNameError(null);
    setSubmitting(false);
    setWriteBack(null);
    setSavedId(plan?.id ?? null);
    setDrift([]);
    // A plan saved while costs were free-form: rows named like no slot fold
    // into "Biaya lain-lain" and their names go to the note (saved on Simpan).
    const planCosts = plan ? planCostItems(plan) : [];
    setCostRows(toCostDrafts(planCosts));
    setNotes(foldedCostNote(plan?.notes ?? '', planCosts));
    setBbmFixOverride(plan?.bbmFixOverride != null ? moneyDigits(plan.bbmFixOverride) : '');
    // `chargedPositioning` only seeds the derived `pool` cache of `fromPlan`
    // (every reader re-derives from position, CR-8b); re-running on a late
    // rule load would throw away the draft ops is editing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plan]);

  // Edit mode: pull the plan's drift (snapshot ≠ current lane) so ops can
  // one-click refresh the stale stops (CR2-D4).
  useEffect(() => {
    if (!open || !plan) return;
    let cancelled = false;
    routePlansAPI
      .get(plan.id)
      .then((detail) => !cancelled && setDrift(detail.drift ?? []))
      .catch(() => !cancelled && setDrift([]));
    return () => {
      cancelled = true;
    };
  }, [open, plan]);

  // CR-5b: workflow drift is advisory (a tag on the stop, via the builder);
  // only ADDRESS drift feeds the alert + "Refresh dari lane".
  const addressDrift = drift.filter((d) => !isWorkflowDrift(d));

  const refreshFromLane = () => {
    setDraft((prev) => {
      const stops = [...prev.stops];
      addressDrift.forEach((d) => {
        const i = d.seq - 1;
        if (i < 0 || i >= stops.length) return;
        const field = d.field as keyof (typeof stops)[number];
        stops[i] = { ...stops[i], [field]: d.current } as (typeof stops)[number];
      });
      return { ...prev, stops };
    });
    setDrift((prev) => prev.filter(isWorkflowDrift));
  };

  const clearError = (key: string) =>
    setErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });

  const validate = (): boolean => {
    const found = planDraftErrors(draft, chargedPositioning ?? false);
    setErrors(found);
    if (!name.trim()) {
      setNameError('Nama rute wajib diisi');
      return false;
    }
    setNameError(null);
    return Object.keys(found).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    if (clientId == null) {
      setNameError('Pilih client dulu');
      return;
    }
    setSubmitting(true);
    setWriteBack(null);
    const stops = toStopsPayload(draft, chargedPositioning ?? false);
    const legs = toLegsPayload(draft);
    const costItems = toCostItemsPayload(costRows);
    const trimmedNotes = notes.trim() || null;
    const bbmOverride = bbmFixOverride && Number(bbmFixOverride) > 0 ? bbmFixOverride : null;
    try {
      const result =
        savedId
          ? await routePlansAPI.update(savedId, {
              name: name.trim(),
              stops,
              legs,
              costItems,
              notes: trimmedNotes,
              bbmFixOverride: bbmOverride,
            })
          : await routePlansAPI.create({
              clientId,
              name: name.trim(),
              stops,
              legs,
              costItems,
              notes: trimmedNotes,
              bbmFixOverride: bbmOverride,
            });
      setSavedId(result.plan.id);
      const failed = result.laneWriteBack?.failed?.length ?? 0;
      if (failed > 0) {
        // Plan is saved; the lanes need a retry (CR2-D8) — keep the drawer open.
        setWriteBack(result.laneWriteBack ?? null);
        setSubmitting(false);
        return;
      }
      toast.success(
        editing ? 'Rute diperbarui' : `Rute “${result.plan.name}” tersimpan`
      );
      onSaved(result.plan);
      onClose();
    } catch (err) {
      setSubmitting(false);
      if (err instanceof RoutePlanApiError && err.status === 409) {
        setNameError('Nama rute sudah dipakai client ini');
        return;
      }
      toast.error('Gagal menyimpan rute', {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const footer = (
    <div className="flex items-center justify-end gap-2">
      <Button tone="neutral" style="stroke" onClick={onClose} disabled={submitting}>
        Batal
      </Button>
      <Button onClick={save} loading={submitting} disabled={submitting}>
        {editing ? 'Simpan perubahan' : 'Simpan rute'}
      </Button>
    </div>
  );

  return (
    <FormModal
      open={open}
      onClose={submitting ? () => undefined : onClose}
      icon={<RiRoadMapLine />}
      title={editing ? 'Ubah rute' : 'Buat rute baru'}
      subtitle={
        clientName ? `Rute untuk ${clientName}` : 'Template rute per client'
      }
      size="xl"
      position="right"
      footer={footer}
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
    >
      <div className="space-y-5">
        <Field>
          <Label htmlFor="route-plan-name" required>
            Nama rute
          </Label>
          <InputRoot size="md" invalid={!!nameError}>
            <Input
              className="min-w-0 w-full"
              id="route-plan-name"
              value={name}
              placeholder="mis. Pool → Patimban → Karawang → Pool"
              onChange={(e) => {
                setName(e.target.value);
                setNameError(null);
              }}
            />
          </InputRoot>
          {nameError && <Hint tone="error">{nameError}</Hint>}
        </Field>

        {addressDrift.length > 0 && (
          <Alert
            status="information"
            appearance="lighter"
            size="sm"
            title={`${addressDrift.length} alamat berubah di lane sejak rute ini disimpan`}
            data-testid="drift-alert"
          >
            <div className="mt-1 flex items-center justify-between gap-2">
              <span className="text-[12px] text-text-sub-600">
                Snapshot rute berbeda dari master alamat sekarang.
              </span>
              <Button
                tone="neutral"
                style="stroke"
                size="xs"
                onClick={refreshFromLane}
              >
                Refresh dari lane
              </Button>
            </div>
          </Alert>
        )}

        {writeBack && writeBack.failed.length > 0 && (
          <Alert
            status="warning"
            appearance="lighter"
            size="sm"
            title={`Rute tersimpan · ${writeBack.failed.length} alamat belum masuk Addresses`}
          >
            <div className="mt-1 flex items-center justify-between gap-2">
              <span className="text-[12px] text-text-sub-600">
                Stop manual belum tersimpan sebagai lane DRAFT.
              </span>
              <Button
                tone="neutral"
                style="stroke"
                size="xs"
                onClick={save}
                loading={submitting}
              >
                Coba lagi
              </Button>
            </div>
          </Alert>
        )}

        <RoutePlanBuilder
          value={draft}
          onChange={setDraft}
          errors={errors}
          onClearError={clearError}
          clientId={clientId}
          chargedPositioning={chargedPositioning}
          disabled={submitting}
          enableLegs={enableLegs}
          drift={drift}
        />

        <RoutePlanCostItems
          value={costRows}
          onChange={setCostRows}
          notes={notes}
          onNotesChange={setNotes}
          bbmFixOverride={bbmFixOverride}
          onBbmFixOverrideChange={setBbmFixOverride}
          disabled={submitting}
        />
      </div>
    </FormModal>
  );
};

export default RoutePlanDrawer;
